import { useMemo, useState } from 'react'
import { constants, TIMER_RESEND } from 'config/constants'

import { useAppSelector } from 'store/hooks'
import { selectedUser } from 'store/reducers/users'
import { getVerifiedToken, resendVerificationCode } from 'store/fetchers/utils'

import { useTranslation } from 'utils/use-translation'
import ConfirmData from '../confirm-data'
import CodeVerificationInput from 'components/global/code-verification-input'

export const Confirm = ({
  currencyInfo,
  address,
  verificationToken,
  setVerificationToken,
  handleNext,
  onBack,
  errorCode,
  extraId,
  setErrorCode = (v: any) => v,
}) => {
  const { t, replacePlaceholders } = useTranslation()

  const user = useAppSelector(selectedUser)
  const credential = useMemo(() => {
    if (user.email) {
      return user.email
    }
    if (user.phone) {
      return user.phone
    }
    return ''
  }, [user])

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isValidConfirmCode, setIsValidConfirmCode] = useState<boolean>(null)
  const [confirmCode, setConfirmCode] = useState('')

  const onResend = async (captcha: string): Promise<void> => {
    setIsLoading(true)

    setVerificationToken(
      await resendVerificationCode({
        verification_token: verificationToken,
        captcha: captcha,
      }),
    )

    if (!verificationToken) {
      setIsValidConfirmCode(null)
      setConfirmCode('')
    }

    setIsLoading(false)
  }

  const handleContinue = async (code: string) => {
    setIsLoading(true)

    try {
      const res = await getVerifiedToken({
        code: code,
        verification_token: verificationToken,
      })

      if (res.result) {
        const response = res.response

        if (!response) {
          setIsValidConfirmCode(false)

          setErrorCode(res?.message.includes('invalid')
            ? t('code_invalid_try_again_later')
            : res?.message
          )
        } else {
          setIsValidConfirmCode(true)
          setErrorCode(null)
        }
        await handleNext(response)
      } else {
        setIsValidConfirmCode(false)
        setErrorCode(res.message)
      }
    } catch (err) {
      setIsLoading(false)
      setIsValidConfirmCode(false)
      
      const msg = err?.response?.message || err?.response?.error || ''

      if (msg.includes('limit')) {
        setErrorCode(t('rate_limit_exceeded_retry_later'))
      } else if (msg.includes('invalid') || msg.includes('not valid')
      ) {
        setErrorCode(t('code_invalid_try_again_later'))
      } else {
        setErrorCode('INVALID_REQUEST_PARAMS')
      }
    }
  }

  return (
    <>
      <ConfirmData
        currencyInfo={currencyInfo}
        address={address}
        hasTag={Boolean(currencyInfo?.is_extra_id_exists)}
        tag={extraId}
      />

      <section>
        <h2>{t('verification')} <span>1/2</span></h2>

        <p>
          {replacePlaceholders(t('enter_length_code_sent_to'),
            { length: constants.codeLength })}: <span>{credential}</span>
        </p>

        <CodeVerificationInput
          code={confirmCode}
          setCode={setConfirmCode}
          isCodeValid={isValidConfirmCode}
          setIsCodeValid={setIsValidConfirmCode}
          errorMsg={errorCode}
          setErrorMsg={setErrorCode}
          isLoading={isLoading}
          countSeconds={TIMER_RESEND}
          resendBtn
          onResendCode={onResend}
          verifyCode={handleContinue}
          resendWithCaptcha
          showError
        />
      </section>
    </>
  )
}
